"use client";

import {
  useCallback,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
} from "react";

export type CompareOrientation = "horizontal" | "vertical";

const START = 50;
const STEP = 2;
const BIG_STEP = 10;

const clamp = (value: number) => Math.min(100, Math.max(0, value));

type UseCompareSliderOptions = {
  orientation: CompareOrientation;
  frameRef: RefObject<HTMLDivElement | null>;
  handleRef: RefObject<HTMLDivElement | null>;
  /** Where the divider starts, in percent (default 50). */
  initialPosition?: number;
};

/**
 * Shared drag, tap, touch, and keyboard logic for before/after dividers.
 * Horizontal: move with X; vertical page scroll stays on touch (pan-y).
 * Vertical: move with Y; horizontal page scroll stays on touch (pan-x).
 */
export function useCompareSlider({
  orientation,
  frameRef,
  handleRef,
  initialPosition = START,
}: UseCompareSliderOptions) {
  const [position, setPosition] = useState(initialPosition);
  const dragging = useRef(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const moveTo = useCallback(
    (clientX: number, clientY: number) => {
      const frame = frameRef.current;
      if (!frame) return;
      const rect = frame.getBoundingClientRect();
      if (orientation === "horizontal") {
        if (rect.width === 0) return;
        setPosition(clamp(((clientX - rect.left) / rect.width) * 100));
      } else {
        if (rect.height === 0) return;
        setPosition(clamp(((clientY - rect.top) / rect.height) * 100));
      }
    },
    [frameRef, orientation],
  );

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.pointerType === "touch") {
      touchStart.current = { x: event.clientX, y: event.clientY };
      return;
    }
    dragging.current = true;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Pointer already released; move events still work.
    }
    moveTo(event.clientX, event.clientY);
    handleRef.current?.focus({ preventScroll: true });
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const start = touchStart.current;
    if (start && !dragging.current) {
      const dx = Math.abs(event.clientX - start.x);
      const dy = Math.abs(event.clientY - start.y);
      if (orientation === "horizontal") {
        if (dx < 6 || dx < dy) return;
      } else if (dy < 6 || dy < dx) return;
      dragging.current = true;
    }
    if (dragging.current) moveTo(event.clientX, event.clientY);
  };

  const stopDragging = () => {
    dragging.current = false;
    touchStart.current = null;
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const start = touchStart.current;
    if (start && !dragging.current) {
      const moved =
        Math.abs(event.clientX - start.x) + Math.abs(event.clientY - start.y);
      if (moved < 10) moveTo(event.clientX, event.clientY);
    }
    stopDragging();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? BIG_STEP : STEP;
    const next: Record<string, number> = {
      ArrowLeft: position - step,
      ArrowDown: position + step,
      ArrowRight: position + step,
      ArrowUp: position - step,
      Home: 0,
      End: 100,
    };
    if (next[event.key] === undefined) return;
    event.preventDefault();
    setPosition(clamp(next[event.key]));
  };

  const rounded = Math.round(position);

  return {
    position,
    rounded,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: stopDragging,
    onLostPointerCapture: stopDragging,
    onKeyDown,
  };
}
